import {Text} from '@rdcomponents/text/text';
import {Flex, Table} from 'antd';
import {ColumnFilterItem} from 'antd/es/table/interface';
import {useCallback, useMemo} from 'react';

import {TableFilterDropdown} from '../../../../../layout/table-filter-dropdown/table-filter-dropdown';
import {Trend} from '../../../../../layout/trend/trend';
import {Locale} from '../../../../../provider/locale/localization';
import {AverageResponseTimeByEmployeeType} from '../../../../../service/reviews-and-answers-statistics/average-response-time-by-empoyee/average-response-time-by-employee-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {useTableSorter} from '../../../../../util/antd/table/table-sorter-hook';
import {useFormat} from '../../../../../util/format-hook/format-hook';
import {AverageResponseTimeTable} from '../average-response-time-table/average-response-time-table';

import * as styles from './employee-stats-table.scss';

type PropsType = {
    pdfPreview?: boolean;
    averageResponseTimeByEmployee: Array<AverageResponseTimeByEmployeeType>;
    isLoading?: boolean;
    timeUnit: ReviewsAndAnswersStatisticsUnitMeasurementEnum;
    isCompareMode: boolean;
};

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
export function EmployeeStatsTable(props: PropsType): JSX.Element {
    const {pdfPreview, averageResponseTimeByEmployee, isLoading, timeUnit, isCompareMode} = props;

    const getRowKey = useCallback((record: AverageResponseTimeByEmployeeType) => String(record.employee.pk), []);
    const {getFormattedNumber} = useFormat();

    const employeeFilters: Array<ColumnFilterItem> = useMemo(() => {
        return averageResponseTimeByEmployee.map((item) => {
            return {
                text: item.employee.name,
                value: getRowKey(item),
            };
        });
    }, [averageResponseTimeByEmployee, getRowKey]);

    const {getSortOrder, setSorterOrder} = useTableSorter();

    return (
        <AverageResponseTimeTable<AverageResponseTimeByEmployeeType>
            dataSource={averageResponseTimeByEmployee || []}
            getSortOrder={getSortOrder}
            isCompareMode={isCompareMode}
            isLoading={isLoading}
            pdfPreview={pdfPreview}
            rowKey={getRowKey}
            setSorterState={setSorterOrder}
            timeUnit={timeUnit}
        >
            <Table.Column<AverageResponseTimeByEmployeeType>
                // eslint-disable-next-line no-undefined
                filterDropdown={pdfPreview ? undefined : TableFilterDropdown}
                filters={pdfPreview ? [] : employeeFilters}
                onFilter={(recordKey, record) => getRowKey(record) === recordKey}
                render={(_value, record) => {
                    return (
                        <Flex vertical>
                            <Text block bolder>
                                {record.employee.name}
                            </Text>
                            <Text block secondary>
                                {record.employee.email || '–'}
                            </Text>
                        </Flex>
                    );
                }}
                title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__TABLE__EMPLOYEE" />}
            />
            {isCompareMode ? (
                <Table.ColumnGroup<AverageResponseTimeByEmployeeType>
                    title={
                        <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__TABLE__TAGS_COUNT" />
                    }
                >
                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="right"
                        dataIndex="tagsCountValue"
                        render={(_value, record) => {
                            return <Text block>{record.tagsCount.value}</Text>;
                        }}
                        sortOrder={getSortOrder('tagsCountValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) => (record1.tagsCount.value || 0) - (record2.tagsCount.value || 0)
                        }
                        title="A"
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="center"
                        className={styles.EmployeeStatsTable_deltaColumn}
                        dataIndex="tagsCountDelta"
                        render={(_value, record) => {
                            return <Trend delta={record.tagsCount.delta || 0} />;
                        }}
                        sortOrder={getSortOrder('tagsCountDelta')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) => (record1.tagsCount.delta || 0) - (record2.tagsCount.delta || 0)
                        }
                        title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />}
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        dataIndex="tagsCountComparedValue"
                        render={(_value, record) => {
                            return <Text block>{record.tagsCount.comparedValue}</Text>;
                        }}
                        sortOrder={getSortOrder('tagsCountComparedValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.tagsCount.comparedValue ?? 0) - (record2.tagsCount.comparedValue ?? 0)
                        }
                        title="B"
                    />
                </Table.ColumnGroup>
            ) : (
                <Table.Column<AverageResponseTimeByEmployeeType>
                    align="center"
                    render={(_value, record) => {
                        return <Text block>{record.tagsCount.value}</Text>;
                    }}
                    sorter={
                        pdfPreview
                            ? false
                            : (record1, record2) => (record1.tagsCount.value || 0) - (record2.tagsCount.value || 0)
                    }
                    title={
                        <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__TABLE__TAGS_COUNT" />
                    }
                />
            )}

            {isCompareMode ? (
                <Table.ColumnGroup<AverageResponseTimeByEmployeeType>
                    title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__ABUSES" />}
                >
                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="right"
                        dataIndex="abusesValue"
                        render={(_value, record) => {
                            return <Text block>{record.abusesCount.value}</Text>;
                        }}
                        sortOrder={getSortOrder('abusesValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.abusesCount.value || 0) - (record2.abusesCount.value || 0)
                        }
                        title="A"
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="center"
                        className={styles.EmployeeStatsTable_deltaColumn}
                        dataIndex="abusesDelta"
                        render={(_value, record) => {
                            return <Trend delta={record.abusesCount.delta || 0} />;
                        }}
                        sortOrder={getSortOrder('abusesDelta')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.abusesCount.delta || 0) - (record2.abusesCount.delta || 0)
                        }
                        title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />}
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        dataIndex="abusesComparedValue"
                        render={(_value, record) => {
                            return <Text block>{record.abusesCount.comparedValue}</Text>;
                        }}
                        sortOrder={getSortOrder('abusesComparedValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.abusesCount.comparedValue ?? 0) -
                                      (record2.abusesCount.comparedValue ?? 0)
                        }
                        title="B"
                    />
                </Table.ColumnGroup>
            ) : (
                <Table.Column<AverageResponseTimeByEmployeeType>
                    align="center"
                    render={(_value, record) => {
                        return <Text block>{record.abusesCount.value}</Text>;
                    }}
                    sorter={
                        pdfPreview
                            ? false
                            : (record1, record2) => (record1.abusesCount.value || 0) - (record2.abusesCount.value || 0)
                    }
                    title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__ABUSES" />}
                />
            )}

            {isCompareMode ? (
                <Table.ColumnGroup<AverageResponseTimeByEmployeeType>
                    title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__ACCEPTED_ABUSES" />}
                >
                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="right"
                        dataIndex="acceptedAbusesValue"
                        render={(_value, record) => {
                            return <Text block>{record.acceptedAbusesCount.value}</Text>;
                        }}
                        sortOrder={getSortOrder('acceptedAbusesValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.acceptedAbusesCount.value || 0) -
                                      (record2.acceptedAbusesCount.value || 0)
                        }
                        title="A"
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="center"
                        className={styles.EmployeeStatsTable_deltaColumn}
                        dataIndex="acceptedAbusesDelta"
                        render={(_value, record) => {
                            return <Trend delta={record.acceptedAbusesCount.delta || 0} />;
                        }}
                        sortOrder={getSortOrder('acceptedAbusesDelta')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.acceptedAbusesCount.delta || 0) -
                                      (record2.acceptedAbusesCount.delta || 0)
                        }
                        title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />}
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        dataIndex="acceptedAbusesComparedValue"
                        render={(_value, record) => {
                            return <Text block>{record.abusesCount.comparedValue}</Text>;
                        }}
                        sortOrder={getSortOrder('acceptedAbusesComparedValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.abusesCount.comparedValue ?? 0) -
                                      (record2.abusesCount.comparedValue ?? 0)
                        }
                        title="B"
                    />
                </Table.ColumnGroup>
            ) : (
                <Table.Column<AverageResponseTimeByEmployeeType>
                    align="center"
                    render={(_value, record) => {
                        return <Text block>{record.acceptedAbusesCount.value}</Text>;
                    }}
                    sorter={
                        pdfPreview
                            ? false
                            : (record1, record2) =>
                                  (record1.acceptedAbusesCount.value || 0) - (record2.acceptedAbusesCount.value || 0)
                    }
                    title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__ACCEPTED_ABUSES" />}
                />
            )}
            {isCompareMode ? (
                <Table.ColumnGroup<AverageResponseTimeByEmployeeType>
                    title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__ASSIGNED_REVIEWS" />}
                >
                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="right"
                        dataIndex="assignedReviewsValue"
                        render={(_value, record) => {
                            return <Text block>{record.assignedReviewsCount.value}</Text>;
                        }}
                        sortOrder={getSortOrder('assignedReviewsValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.assignedReviewsCount.value || 0) -
                                      (record2.assignedReviewsCount.value || 0)
                        }
                        title="A"
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="center"
                        className={styles.EmployeeStatsTable_deltaColumn}
                        dataIndex="assignedReviewsDelta"
                        render={(_value, record) => {
                            return <Trend delta={record.assignedReviewsCount.delta || 0} />;
                        }}
                        sortOrder={getSortOrder('assignedReviewsDelta')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.assignedReviewsCount.delta || 0) -
                                      (record2.assignedReviewsCount.delta || 0)
                        }
                        title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />}
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        dataIndex="assignedReviewsComparedValue"
                        render={(_value, record) => {
                            return <Text block>{record.assignedReviewsCount.comparedValue}</Text>;
                        }}
                        sortOrder={getSortOrder('assignedReviewsComparedValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.assignedReviewsCount.comparedValue ?? 0) -
                                      (record2.assignedReviewsCount.comparedValue ?? 0)
                        }
                        title="B"
                    />
                </Table.ColumnGroup>
            ) : (
                <Table.Column<AverageResponseTimeByEmployeeType>
                    align="center"
                    render={(_value, record) => {
                        return <Text block>{record.assignedReviewsCount.value}</Text>;
                    }}
                    sorter={
                        pdfPreview
                            ? false
                            : (record1, record2) =>
                                  (record1.assignedReviewsCount.value || 0) - (record2.assignedReviewsCount.value || 0)
                    }
                    title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__ASSIGNED_REVIEWS" />}
                />
            )}

            {isCompareMode ? (
                <Table.ColumnGroup<AverageResponseTimeByEmployeeType>
                    title={
                        <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__PROCESSED_REVIEWS" />
                    }
                >
                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="right"
                        dataIndex="processedReviewsValue"
                        render={(_value, record) => {
                            return `${getFormattedNumber(record.processedReviewsRatio.value || 0, {
                                maximumFractionDigits: 2,
                            })} %`;
                        }}
                        sortOrder={getSortOrder('processedReviewsValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.processedReviewsRatio.value || 0) -
                                      (record2.processedReviewsRatio.value || 0)
                        }
                        title="A"
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        align="center"
                        className={styles.EmployeeStatsTable_deltaColumn}
                        dataIndex="processedReviewsDelta"
                        render={(_value, record) => {
                            return <Trend delta={record.processedReviewsRatio.delta || 0} />;
                        }}
                        sortOrder={getSortOrder('processedReviewsDelta')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.processedReviewsRatio.delta || 0) -
                                      (record2.processedReviewsRatio.delta || 0)
                        }
                        title={<Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />}
                    />

                    <Table.Column<AverageResponseTimeByEmployeeType>
                        dataIndex="processedReviewsComparedValue"
                        render={(_value, record) => {
                            return `${getFormattedNumber(record.processedReviewsRatio.comparedValue || 0, {
                                maximumFractionDigits: 2,
                            })} %`;
                        }}
                        sortOrder={getSortOrder('processedReviewsComparedValue')}
                        sorter={
                            pdfPreview
                                ? false
                                : (record1, record2) =>
                                      (record1.processedReviewsRatio.comparedValue ?? 0) -
                                      (record2.processedReviewsRatio.comparedValue ?? 0)
                        }
                        title="B"
                    />
                </Table.ColumnGroup>
            ) : (
                <Table.Column<AverageResponseTimeByEmployeeType>
                    align="center"
                    render={(_value, record) => {
                        return `${getFormattedNumber(record.processedReviewsRatio.value || 0, {
                            maximumFractionDigits: 2,
                        })} %`;
                    }}
                    sorter={
                        pdfPreview
                            ? false
                            : (record1, record2) =>
                                  (record1.processedReviewsRatio.value || 0) -
                                  (record2.processedReviewsRatio.value || 0)
                    }
                    title={
                        <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__PROCESSED_REVIEWS" />
                    }
                />
            )}
        </AverageResponseTimeTable>
    );
}
